/**
 * DOC-03 — client-side PDF first-page thumbnail (Q3 RESOLVED: no server-side
 * render / imagick; Gotenberg is HTML→PDF only). Renders a PDF's first page to
 * a canvas with pdfjs-dist and returns a PNG data-URL the document row can show
 * inline.
 *
 * Worker config (RESEARCH Pitfall #5 — avoid the "fake worker" warning): the
 * worker bundle is imported via Vite's `?url` suffix so Vite emits it as a real
 * asset and `GlobalWorkerOptions.workerSrc` points at the served URL. Without
 * this pdfjs falls back to an in-thread fake worker (slow + a console warning).
 *
 * Lazy / on-demand: `renderFirstPage` is only ever called when a PDF row mounts
 * or scrolls into view (the DocumentCard wires an IntersectionObserver) — the
 * pdfjs module itself is dynamically imported on first use so the ~1MB worker +
 * core never load on a boat that has no PDF documents.
 */

// pdfjs-dist v5 ships ESM under build/. The dynamic import keeps the heavy core
// out of the initial bundle; the worker is a Vite `?url` asset (see below).
type PdfModule = typeof import('pdfjs-dist');

let pdfjsPromise: Promise<PdfModule> | null = null;

/**
 * Lazily import pdfjs-dist and configure its worker ONCE. Subsequent calls
 * reuse the resolved module promise.
 */
async function loadPdfjs(): Promise<PdfModule> {
  if (!pdfjsPromise) {
    pdfjsPromise = (async () => {
      const pdfjs = await import('pdfjs-dist');
      // Vite `?url` import → the worker is emitted as a hashed static asset and
      // this resolves to its served URL (real worker, not the fake fallback).
      const workerSrc = (
        await import('pdfjs-dist/build/pdf.worker.min.mjs?url')
      ).default;
      pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;
      return pdfjs;
    })();
  }
  return pdfjsPromise;
}

export interface RenderFirstPageOptions {
  /**
   * Target longest-edge size in CSS pixels for the rendered thumbnail. The page
   * is scaled to fit this box; defaults to 320 (comfortably covers the `h-40`
   * grid thumbnail at 2× DPR).
   */
  maxEdge?: number;
}

/**
 * Render the first page of the PDF at `url` to a PNG data-URL.
 *
 * `url` is the auth-gated document download/inline stream (45-03) — pdfjs
 * fetches it with the browser's credentials, so no raw MinIO URL is ever held
 * client-side (threat T-45-14: no new exposure beyond what the user can already
 * stream). Returns `null` on any failure so the caller falls back to a type
 * icon (degraded, never broken — RESEARCH §Environment Availability).
 */
export async function renderFirstPage(
  url: string,
  { maxEdge = 320 }: RenderFirstPageOptions = {},
): Promise<string | null> {
  try {
    const pdfjs = await loadPdfjs();

    const loadingTask = pdfjs.getDocument({
      url,
      // Send the session cookie so the auth-gated stream authorizes the fetch.
      withCredentials: true,
    });
    const pdf = await loadingTask.promise;

    try {
      const page = await pdf.getPage(1);

      const baseViewport = page.getViewport({ scale: 1 });
      const scale = Math.min(
        maxEdge / baseViewport.width,
        maxEdge / baseViewport.height,
      );
      const viewport = page.getViewport({ scale: scale > 0 ? scale : 1 });

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) {
        return null;
      }
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);

      await page.render({ canvas, canvasContext: context, viewport }).promise;

      const dataUrl = canvas.toDataURL('image/png');
      return dataUrl;
    } finally {
      // Release the worker-side document handle.
      void pdf.cleanup();
      void pdf.destroy();
    }
  } catch {
    // Corrupt PDF, network/auth failure, or an unsupported file → fall back to
    // the type icon. A thumbnail is a nicety, never load-bearing.
    return null;
  }
}
